import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const { FiEdit3, FiLayout, FiBookOpen, FiImage, FiAirplay, FiFileText } = FiIcons;

const ServicesPage = () => {
  const services = [
    {
      icon: FiEdit3,
      title: 'Editing Naskah & Buku',
      description: 'Penyuntingan profesional untuk naskah jurnal, buku, dan dokumen akademik lainnya. Kami fokus pada kejelasan, alur, dan kepatuhan terhadap gaya selingkung.',
      features: ['Grammar & Spelling Check', 'Academic Tone Adjustment', 'Reference Consistency', 'Style Guide Compliance']
    },
    {
      icon: FiLayout,
      title: 'Layout Jurnal & Buku',
      description: 'Tata letak profesional sesuai standar jurnal internasional (Q1-Q4) dan penerbit akademik ternama.',
      features: ['International Template Setup', 'Table & Figure Formatting', 'Typography Optimization', 'Print-Ready Export']
    },
    {
      icon: FiBookOpen,
      title: 'Desain Cover Institusional',
      description: 'Desain cover buku akademik yang elegan dan mencerminkan kualitas substansi di dalamnya.',
      features: ['Institutional Branding', 'Minimalist Aesthetics', 'High-Res Output', 'Custom Illustrations']
    },
    {
      icon: FiImage,
      title: 'Desain Poster Ilmiah',
      description: 'Transformasi data penelitian Bapak/Ibu menjadi poster yang informatif dan menarik untuk konferensi.',
      features: ['Data Visualization', 'Effective Hierarchy', 'Clear Typography', 'Standard Poster Sizes']
    },
    {
      icon: FiAirplay,
      title: 'Format Presentasi Akademik',
      description: 'Penyusunan slide presentasi yang mendukung penyampaian materi riset secara efektif dan profesional.',
      features: ['Clean Master Slides', 'Visual Storytelling', 'Animation Restraint', 'Legible Content Delivery']
    },
    {
      icon: FiFileText,
      title: 'Parafrase & Formatting',
      description: 'Layanan formatting dokumen sesuai standar institusi dan parafrase etis untuk menjaga integritas naskah.',
      features: ['Ethical Paraphrasing', 'Citation Management', 'Plagiarism Awareness', 'Standard Formatting (APA, MLA, etc.)']
    }
  ];

  return (
    <div className="bg-white font-inter">
      {/* Header Section - Updated to a slightly deeper, elegant slate gradient for better definition */}
      <section className="bg-gradient-to-b from-slate-200/50 via-slate-100/20 to-white py-24 border-b border-divider relative overflow-hidden">
        {/* Subtle background decorative element */}
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none"></div>

        <div className="max-w-6xl mx-auto px-4 text-center relative z-10">
          <motion.h1 
            className="text-4xl md:text-5xl font-extrabold text-primary mb-8 tracking-tight"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Layanan Kami
          </motion.h1>
          <motion.p 
            className="text-xl md:text-2xl text-secondary max-w-3xl mx-auto leading-relaxed font-medium"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Dukungan teknis profesional untuk menunjang produktivitas dan kualitas publikasi akademik Bapak/Ibu.
          </motion.p>
        </div>
      </section>

      {/* Services Detail Grid */}
      <section className="py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-20">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="flex flex-col group"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex items-center gap-5 mb-8">
                  <div className="w-14 h-14 border border-divider rounded-xl flex items-center justify-center bg-gray-50 group-hover:bg-primary group-hover:border-primary transition-all duration-300">
                    <SafeIcon icon={service.icon} className="h-7 w-7 text-primary group-hover:text-white transition-colors duration-300" />
                  </div>
                  <h2 className="text-2xl font-bold text-primary tracking-tight">{service.title}</h2>
                </div>
                
                <p className="text-secondary text-lg leading-relaxed mb-10 font-normal">
                  {service.description}
                </p>
                
                <div className="mt-auto pt-8 border-t border-divider">
                  <h4 className="text-xs font-bold text-primary uppercase tracking-[0.2em] mb-5">Fitur Layanan:</h4>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="flex items-center text-sm text-secondary font-medium">
                        <div className="w-1.5 h-1.5 bg-primary/30 rounded-full mr-3 shrink-0"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom Spacer */}
      <div className="py-12 bg-white"></div>
    </div>
  );
};

export default ServicesPage;